"use client"
import logo from "@/public/images/RM_logo_V2.png"
import logoMini from "@/public/images/RM_logo_V2_mini.svg"
import dynamic from "next/dynamic"
import Image from "next/image"
import Link from "next/link"
import React, {useState} from "react"
import AppButton from "@/app/components/atomic/molecules/AppButton";
import {Products} from "@/app/enums/Products"
import {getThemeColors, ThemeCocoon, toVariant} from "@/app/constants/theme"
import {AFFILIATED_LINK} from "@/app/(pages)/taux-pret-immobilier/utils"
import {PRETTO_UTM_CONTENT, PRETTO_UTM_TERM} from "@/app/constants/prettoJourney"
import {useAffiliatedLink} from "@/app/(pages)/taux-pret-immobilier/hooks/useAffiliatedLink"
import {useTracking} from "@/app/contexts/tracking-context"

const AppIcon = dynamic(() => import("@/app/components/atomic/AppIcon"))

const MODULES = ["Logo", "Nav", "Actu"] as const

type Module = (typeof MODULES)[number] | React.FC

interface WithSections {
    start?: Module[]
    center?: Module[]
    end?: Module[]

    [key: string]: Module[] | undefined
}

export interface IBaseHeader extends WithSections {
    [key: string]: any

    comparatorStyle?: boolean
    classes?: string
}

const SECTIONS_CLASSES: { [key: string]: string } = {
    start: "justify-self-start text-left",
    center: "justify-self-center text-center justify-center",
    end: "justify-self-end text-end justify-end",
}

function BaseHeader(props: IBaseHeader) {
    if (props.comparatorStyle) {
        return (
            <div className={`flex justify-center items-center min-h-20 h-[80px] ${props.classes ?? ""}`}>
                <header
                    className="fixed top-0 left-0 right-0 bg-white shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.1)] mx-auto min-h-20 h-[80px] w-full z-50"
                >
                    <div className="flex w-full h-full items-center justify-between max-w-[1200px] mx-auto px-6 py-4">
                        <Link href="/" className="overflow-hidden" aria-label="Reassurez-moi - Accueil">
                            <Image
                                src={logo}
                                alt="Reassurez-moi - Comparateur d'assurance"
                                height={38}
                                className="h-[38px] w-auto"
                            />
                        </Link>
                        <span className="block text-right leading-tight text-[#99a1af] text-[12px] sm:text-sm sm:leading-normal">
                            Le comparateur d'assurance <br className="lg:hidden" /> le mieux noté depuis 2013
                        </span>
                    </div>
                </header>
            </div>
        )
    }

    return (
        <div className={`flex justify-center items-center min-h-20 h-[80px] ${props.classes ?? ""}`}>
            <header
                className="lg:fixed bg-white mx-auto min-h-20 h-[80px] w-full relative z-50"
            >
                <div className="grid grid-cols-[1fr_auto_1fr] w-full h-full content-center items-center max-w-[1400px] mx-auto px-6">
                    {["start", "center", "end"].map((sectionName) => (
                        <RenderedSection
                            key={sectionName}
                            section={props[sectionName]}
                            sectionName={sectionName}
                        />
                    ))}
                </div>
            </header>
        </div>
    )
}

function RenderedSection({section, sectionName}: { section?: Module[]; sectionName: string }) {
    return (
        <div className={`flex flex-row gap-4 items-center ${SECTIONS_CLASSES[sectionName]}`}>
            {section?.map((component: Module, index: number) => {
                const Component = typeof component === "string" ? (BaseHeader as any)[component] : component
                return (
                    <div key={`module-${index}`} className="flex justify-between items-center min-w-fit">
                        <Component />
                    </div>
                )
            })}
        </div>
    )
}

BaseHeader.Logo = React.memo(function Logo({className}: { className?: string }) {
    return (
        <Link href="/">
            <div
                className={`transition-all ease-in-out duration-100 overflow-hidden ${className}`}
            >
                <Image
                    src={logo}
                    alt="Reassurez-moi - Comparateur d'assurance"
                    width={150}
                    className="py-1 md:max-w-none lg:hidden xl:block"
                />
                <Image
                    src={logoMini}
                    alt="Reassurez-moi - Comparateur d'assurance"
                    width={56}
                    className="hidden lg:block xl:hidden"
                />
            </div>
        </Link>
    )
})

function MobileNav() {
    const [isNavOpen, setIsNavOpen] = useState(false)
    const [openSection, setOpenSection] = useState<string | null>(null)
    const affiliatedHref = useAffiliatedLink(false, {content: PRETTO_UTM_CONTENT.HEADER_MOBILE, term: PRETTO_UTM_TERM.PRET_IMMOBILIER})
    const {track} = useTracking()
    React.useEffect(() => {
        if (!isNavOpen) return
        const scrollY = window.scrollY
        const {style} = document.body
        style.position = "fixed"
        style.top = `-${scrollY}px`
        style.left = "0"
        style.right = "0"
        style.width = "100%"
        return () => {
            style.position = ""
            style.top = ""
            style.left = ""
            style.right = ""
            style.width = ""
            window.scrollTo(0, scrollY)
        }
    }, [isNavOpen])

    const closeNav = () => {
        setIsNavOpen(false)
        setOpenSection(null)
    }

    return (
        <nav aria-label="Menu mobile" className="flex justify-end lg:hidden">
            <button
                onClick={() => setIsNavOpen(!isNavOpen)}
                aria-label={isNavOpen ? "Fermer le menu" : "Ouvrir le menu"}
                aria-expanded={isNavOpen}
                className="flex flex-col items-center justify-between cursor-pointer h-10"
            >
                <div className="flex flex-col justify-center items-center w-8 h-[18px] relative">
                    <span
                        className={`block h-[3px] w-4 bg-pale rounded-full absolute transition-all duration-300 ease-in-out
                            ${isNavOpen ? "rotate-45 top-[7px]" : "top-[1px]"}`}
                    />
                    <span
                        className={`block h-[3px] w-4 bg-pale rounded-full absolute top-[7px] transition-all duration-300 ease-in-out
                            ${isNavOpen ? "opacity-0" : "opacity-100"}`}
                    />
                    <span
                        className={`block h-[3px] w-4 bg-pale rounded-full absolute transition-all duration-300 ease-in-out
                            ${isNavOpen ? "-rotate-45 top-[7px]" : "top-[13px]"}`}
                    />
                </div>
                <span className="text-pale/80 text-[10px] font-semibold">
                    {isNavOpen ? "Fermer" : "Menu"}
                </span>
            </button>

            {isNavOpen && (
                <div
                    aria-hidden="true"
                    className="fixed inset-0 top-[80px] bg-white z-40"
                    onClick={() => setIsNavOpen(false)}
                />
            )}

            <div
                className={`fixed top-[80px] left-0 right-0 z-50 bg-white flex flex-col w-full transition-[max-height]
                    duration-500 ease-in-out
                    ${isNavOpen ? "max-h-[calc(100dvh-80px)] overflow-y-auto" : "max-h-0 overflow-hidden"}
                `}
            >

                {isNavOpen &&
                    NAV_LINKS.map((navLink) => {
                        const isOpen = openSection === navLink.label
                        const themeColors = getThemeColors(navLink.cocoon)

                        return (
                            <div key={navLink.label} className="border-b w-full">
                                <button
                                    aria-expanded={isOpen}
                                    className={`w-full flex items-center justify-between p-5 !mx-0
                                        ${isOpen ? "bg-neutral" : ""}`}
                                    onClick={() => setOpenSection(isOpen ? null : navLink.label)}
                                >
                                    <span className="flex items-center gap-2.5 font-medium">
                                        <ProductDot color={themeColors.primary} />
                                        {navLink.label}
                                    </span>
                                    <AppIcon
                                        type="regular"
                                        size="sm"
                                        iconClassName={`fa-chevron-down transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                                        className="text-pale"
                                    />
                                </button>

                                <div
                                    className={`overflow-hidden transition-[max-height] duration-500 ease-in-out bg-pastel-neutral
                                        ${isOpen ? "max-h-[2000px]" : "max-h-0"}`}
                                >
                                    {navLink.categories?.map((category) => (
                                        <div key={category.name} className="flex flex-col items-start w-full px-6 gap-4 py-8">
                                            {category.name !== "Autres" && (
                                                <span className={`text-xl title-look text-${themeColors.secondaryText} bg-${themeColors.secondary1}`}>
                                                    {category.name}
                                                </span>
                                            )}
                                            {category.links.map((link, index) => (
                                                <Link
                                                    key={index}
                                                    href={link.url ?? ""}
                                                    onClick={closeNav}
                                                    className="flex w-full gap-6 items-center"
                                                >
                                                    <div
                                                        className={`bg-${themeColors.secondary2} rounded-xl flex items-center justify-center shrink-0 w-[36px] h-[36px]`}
                                                    >
                                                        <AppIcon type="regular" iconClassName={link.icon} size="xl" />
                                                    </div>

                                                    <span className="w-full text-left">
                                                        {link.label}
                                                    </span>

                                                    <AppIcon type="regular" iconClassName="fa-chevron-right" size="xl" className="text-pale" />
                                                </Link>
                                            ))}
                                        </div>
                                    ))}
                                </div>

                                {isOpen && navLink.underContent && (
                                    <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-neutral border-t p-4">
                                        <div className="flex gap-4 items-center">
                                            <AppIcon
                                                type="regular"
                                                iconClassName={navLink.underContent.icon}
                                                className="text-green-700"
                                            />
                                            <span className="flex-1 text-left text-pale text-sm">{navLink.underContent.label}</span>
                                        </div>
                                        <AppButton
                                            arrowPosition="before"
                                            wFull
                                            size="xs"
                                            href={navLink.underContent.url === AFFILIATED_LINK ? affiliatedHref : navLink.underContent.url}
                                            target={navLink.underContent.isExternal ? "_blank" : "_self"}
                                            onClick={() => {
                                                track({event: "wp_advertizing_button_click", type: "cta_header_menu"})
                                                if (navLink.underContent?.url === AFFILIATED_LINK) {
                                                    track({event: "gtm_partner_link_clic", event_source: "header_menu_cta", partner_url: affiliatedHref})
                                                }
                                                closeNav()
                                            }}
                                        >
                                            {navLink.underContent.ctaLabel}
                                        </AppButton>
                                    </div>
                                )}
                            </div>
                        )
                    })}

                {isNavOpen &&
                    RM_LINKS.map((navLink) => {
                        const isOpen = openSection === navLink.label
                        const allLinks = navLink.categories?.flatMap((c) => c.links) ?? []
                        return (
                            <div key={navLink.label} className="border-b w-full">
                                <button
                                    aria-expanded={isOpen}
                                    className={`w-full flex items-center justify-between p-5 !mx-0
                                        ${isOpen ? "bg-neutral" : ""}`}
                                    onClick={() => setOpenSection(isOpen ? null : navLink.label)}
                                >
                                    <span className="font-medium">
                                        {navLink.label}
                                    </span>
                                    <AppIcon
                                        type="regular"
                                        size="sm"
                                        iconClassName={`fa-chevron-down transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                                        className="text-pale"
                                    />
                                </button>

                                <div
                                    className={`overflow-hidden transition-[max-height] duration-500 ease-in-out bg-pastel-neutral
                                        ${isOpen ? "max-h-[2000px]" : "max-h-0"}`}
                                >
                                    <div className="flex flex-col items-start w-full px-6 gap-4 py-8">
                                        {allLinks.map((link, index) => (
                                            <Link
                                                key={index}
                                                href={link.url ?? ""}
                                                onClick={closeNav}
                                                className="flex w-full gap-6 items-center"
                                            >
                                                <div className="bg-pastel-blue rounded-xl flex items-center justify-center shrink-0 w-[36px] h-[36px]">
                                                    <AppIcon type="regular" iconClassName={link.icon} size="xl" className="text-white" />
                                                </div>

                                                <span className="w-full text-left">
                                                    {link.label}
                                                </span>

                                                <AppIcon type="regular" iconClassName="fa-chevron-right" size="xl" className="text-pale" />
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
            </div>

        </nav>
    );
}

BaseHeader.Nav = function Nav({}) {
    return (
        <>
            <nav aria-label="Navigation principale" className="hidden md:justify-end lg:!flex lg:gap-2 xl:gap-3 lg:items-stretch flex-1">
                {NAV_LINKS.map((navLink) => (
                    <DropDownMenu key={navLink.label} link={navLink} />
                ))}
            </nav>
        </>
    )
}

BaseHeader.Actu = function Actu({}) {
    return (
        <>
            <MobileNav />
            <nav aria-label="A propos" className="hidden md:justify-end lg:!flex lg:items-stretch text-sm xl:text-base relative">
                <AboutDropDown />
            </nav>
        </>
    )
}

const NAV_LINKS: NavLink[] = [
    {
        label: "Assurance emprunteur",
        cocoon: Products.LOAN_INSURANCE,
        icon: "fa-shield-check",
        url: "/simulation-assurance-pret-immobilier",
        underContent: {
            icon: "fa-arrow-trend-down",
            label: "Économisez jusqu’à 250 €/mois en changeant d’assurance de prêt",
            ctaLabel: "Comparer les assurances de prêt",
            url: "/simulation-assurance-pret-immobilier",
        },
        categories: [
            {
                name: "Je compare",
                links: [
                    {label: "Simulation d’assurance de prêt immobilier", url: "/simulation-assurance-pret-immobilier", icon: "fa-house-laptop"},
                    {label: "Calcul assurance emprunteur", url: "/guide/assurance-pret-immobilier/calcul", icon: "fa-calculator"},
                    {label: "Taux d’assurance de prêt immobilier", url: "/guide/assurance-pret-immobilier/taux", icon: "fa-percent"},
                ],
            },
            {
                name: "J’économise",
                links: [
                    {label: "Changer d’assurance emprunteur", url: "/guide/assurance-pret-immobilier/changer", icon: "fa-calculator"},
                    {label: "Renégocier son assurance emprunteur", url: "/guide/assurance-pret-immobilier/renegocier", icon: "fa-arrows-rotate"},
                ],
            },
            {
                name: "Je m’informe",
                links: [
                    {label: "Fonctionnement de l’assurance emprunteur", url: "/guide/assurance-pret-immobilier", icon: "fa-circle-info"},
                    {label: "Délégation d’assurance", url: "/guide/assurance-pret-immobilier/delegation", icon: "fa-handshake"},
                    {
                        label: "Remboursement de l’assurance emprunteur",
                        url: "/guide/assurance-pret-immobilier/remboursement-anticipe",
                        icon: "fa-money-bill-transfer"
                    },
                ],
            },
        ],
    },
    {
        label: "Prêt Immobilier",
        cocoon: Products.LOAN,
        icon: "fa-house",
        url: "/guide/pret-immobilier",
        underContent: {
            icon: "fa-euro",
            label: "Trouvez le meilleur taux immobilier pour votre projet",
            ctaLabel: "Simuler mon prêt immobilier",
            url: AFFILIATED_LINK,
            isExternal: true,
        },
        categories: [
            {
                name: "Je simule un projet",
                links: [
                    {label: "Simulation de prêt immobilier", url: "/guide/pret-immobilier/simulation", icon: "fa-calculator"},
                    {label: "Taux des crédits immobiliers", url: "/taux-pret-immobilier", icon: "fa-percent"},
                    {label: "Calcul des mensualités", url: "/guide/pret-immobilier/calcul-mensualites", icon: "fa-calendar-days"},
                    {label: "Tableau d’amortissement", url: "/guide/pret-immobilier/tableau-amortissement", icon: "fa-table"},
                ],
            },
            {
                name: "Mon projet évolue",
                links: [
                    {label: "Renégocier son prêt immobilier", url: "/guide/pret-immobilier/renegocier", icon: "fa-arrows-rotate"},
                    {label: "Suspendre un prêt immobilier", url: "/guide/pret-immobilier/suspendre", icon: "fa-pause"},
                ],
            },
            {
                name: "Bon à savoir",
                links: [
                    {label: "Prêt immobilier", url: "/guide/pret-immobilier", icon: "fa-house"},
                    {label: "Calculer les frais de notaire", url: "/guide/pret-immobilier/calcul-frais-notaire", icon: "fa-file-signature"},
                ],
            },
        ],
    },
    {
        label: "Mutuelle santé",
        cocoon: Products.HEALTH,
        icon: "fa-heart",
        url: "/comparateur-mutuelle-sante",
        underContent: {
            icon: "fa-euro",
            label: "Économisez jusqu’à 476 €/an sur votre assurance santé",
            ctaLabel: "Comparer les mutuelles santé",
            url: "/comparateur-mutuelle-sante",
        },
        categories: [
            {
                name: "Je compare",
                links: [
                    {label: "Comparateur de mutuelle santé", url: "/comparateur-mutuelle-sante", icon: "fa-heart"},
                    {label: "Devis mutuelle santé", url: "/devis-mutuelle-sante", icon: "fa-file-invoice"},
                    {label: "Mutuelle pas chère", url: "/guide/mutuelle-sante/pas-cher", icon: "fa-tag"},
                    {label: "Mutuelle expatrié", url: "/guide/mutuelle-sante/expatries", icon: "fa-plane"},
                ],
            },
            {
                name: "Je change de mutuelle",
                links: [
                    {label: "Changer de mutuelle santé", url: "/guide/mutuelle-sante/changer", icon: "fa-arrows-rotate"},
                    {label: "Résilier sa mutuelle santé", url: "/guide/mutuelle-sante/resilier", icon: "fa-xmark"},
                ],
            },
            {
                name: "Bien comprendre",
                links: [
                    {label: "Remboursement de la mutuelle santé", url: "/guide/mutuelle-sante/remboursement", icon: "fa-money-bill-transfer"},
                    {
                        label: "Simulateur et calcul remboursement mutuelle",
                        url: "/guide/mutuelle-sante/remboursement/simulateur-remboursement-mutuelle",
                        icon: "fa-calculator"
                    },
                ],
            },
        ],
    },
    {
        label: "Assurance voyage",
        cocoon: Products.TRAVEL,
        icon: "fa-plane",
        iconBgColor: "#66C469",
        url: "/guide/assurance-voyage",
        underContent: {
            icon: "fa-shield-check",
            label: "Partez couvert avec l’assurance voyage adaptée à votre séjour",
            ctaLabel: "Comparer les assurances voyage",
            url: "/guide/assurance-voyage",
        },
        categories: [
            {
                name: "Mon contrat",
                links: [
                    {label: "Assurance voyage", url: "/guide/assurance-voyage", icon: "fa-plane"},
                    {label: "Assurance annulation", url: "/guide/assurance-voyage/annulation-voyage", icon: "fa-calendar-xmark"},
                    {label: "Assurance voyage longue durée", url: "/guide/assurance-voyage/longue-duree", icon: "fa-calendar-days"},
                    {label: "Assurance annuelle", url: "/guide/assurance-voyage/annuelle", icon: "fa-arrows-rotate"},
                ],
            },
            {
                name: "Mon séjour",
                links: [
                    {label: "Assurance voyage USA", url: "/guide/assurance-voyage/usa", icon: "fa-flag-usa"},
                    {label: "Assurance voyage Thaïlande", url: "/guide/assurance-voyage/thailande", icon: "fa-umbrella-beach"},
                    {label: "Assurance Schengen", url: "/guide/assurance-voyage/schengen", icon: "fa-passport"},
                    {label: "Assurance PVT", url: "/guide/assurance-voyage/pvt", icon: "fa-earth-americas"},
                ],
            },
            {
                name: "Mes besoins",
                links: [
                    {label: "Assurance voyage senior", url: "/guide/assurance-voyage/senior", icon: "fa-user"},
                    {label: "Assurance séjour au pair", url: "/guide/assurance-voyage/au-pair", icon: "fa-children"},
                    {label: "Assurance voyage étudiant", url: "/guide/assurance-voyage/etudiant-etranger-en-france", icon: "fa-graduation-cap"},
                    {label: "Assurance voyage pour femme enceinte", url: "/guide/assurance-voyage/grossesse", icon: "fa-baby"},
                ],
            },
        ],
    },
    {
        label: "Prévoyance",
        cocoon: Products.DEATH_INSURANCE,
        icon: "fa-umbrella",
        url: "/guide/prevoyance",
        underContent: {
            icon: "fa-arrow-trend-down",
            label: "Comparez les meilleures assurances du marché au même endroit",
            ctaLabel: "Comparer les assurances prévoyances",
            url: "/guide/prevoyance",
        },
        categories: [
            {
                name: "Je compare",
                links: [
                    {label: "Assurance décès", url: "/guide/assurance-deces", icon: "fa-cross"},
                    {label: "Assurance obsèques", url: "/guide/assurance-obseques", icon: "fa-tombstone"},
                    {label: "Assurance dépendance", url: "/guide/assurance-dependance", icon: "fa-family"},
                ]
            },
            {
                name: "Je choisis",
                links: [
                    {label: "Meilleure assurance décès", url: "/guide/assurance-deces/meilleure", icon: "fa-medal"},
                    {label: "Assurance obsèques pas chère", url: "/guide/assurance-obseques/obseques-pas-cher", icon: "fa-tag"},
                ]
            },
            {
                name: "J’économise",
                links: [
                    {label: "Prix d’une assurance dépendance", url: "/guide/assurance-dependance/tarif", icon: "fa-arrow-trend-down"},
                    {label: "Prix d’une assurance décès", url: "/guide/assurance-deces/prix", icon: "fa-sack-dollar"},
                    {
                        label: "Remboursement frais d’obsèques",
                        url: "/guide/assurance-obseques/remboursement-mutuelle-frais-obseques",
                        icon: "fa-hand-holding-circle-dollar"
                    },
                ]
            },
        ],
    },
]
const RM_LINKS: NavLink[] = [
    {
        label: "A propos",
        url: "/guide/a-propos/offre",
        categories: [
            {
                name: "Autres",
                links: [
                    {label: "Notre mission", url: "/guide/a-propos/offre", icon: "fa-heart"},
                    {label: "Notre équipe", url: "/guide/a-propos/equipe", icon: "fa-building"},
                    {label: "Actualités", url: "/guide/actualites", icon: "fa-calendar"},
                    {label: "Nos partenaires", url: "/guide/partenaires", icon: "fa-umbrella"},
                    {label: "Avis clients", url: "/avis", icon: "fa-file"},
                    {label: "Nous contacter", url: "/contact", icon: "fa-circle-info"},
                ],
            },
        ],
    },
]

interface IUnderContent {
    icon: string
    label: string
    ctaLabel: string
    url: string
    isExternal?: boolean
}

type NavLinkItem = {
    label: string
    url?: string
    icon?: string
}

type NavCategory = {
    name: string
    links: NavLinkItem[]
}

type NavLink = {
    label: string
    url?: string
    cocoon?: ThemeCocoon
    icon?: string
    iconBgColor?: string
    categories?: NavCategory[]
    underContent?: IUnderContent
}

const ABOUT_LINK = RM_LINKS[0]

function AboutDropDown() {
    const links = ABOUT_LINK.categories?.flatMap((c) => c.links) ?? []

    return (
        <ul className="px-2 transition-all group flex flex-nowrap items-center rounded-lg hover:bg-pastel-neutral relative">
            <li>
                <Link
                    href={ABOUT_LINK.url ?? ""}
                    className="flex items-center gap-2.5 whitespace-nowrap py-2 font-medium text-sm"
                    aria-haspopup="true"
                    aria-controls="submenu-a-propos"
                >
                    {ABOUT_LINK.label}
                    <AppIcon type="regular" size="sm" iconClassName="fa-chevron-down" className="hidden lg:block" />
                </Link>

                <div
                    id="submenu-a-propos"
                    role="menu"
                    className="absolute top-full right-0 z-30 h-0
                        lg:group-hover:opacity-100 opacity-0 group-hover:pointer-events-auto group-hover:h-min
                        transition-[opacity] duration-500 ease-in-out pointer-events-none pt-[20px]"
                >
                    <div className="bg-white rounded-3xl shadow-xl overflow-hidden w-[280px] p-4 flex flex-col gap-2">
                        {links.map((link) => (
                            <Link
                                key={link.label}
                                href={link.url ?? ""}
                                role="menuitem"
                                className="flex w-full gap-4 items-center cursor-pointer
                                    hover:bg-pastel-neutral rounded-lg px-2 py-1.5 transition-all duration-200 ease-in-out"
                            >
                                <div className="bg-rm-blue rounded-xl flex items-center justify-center shrink-0 w-[36px] h-[36px]">
                                    <AppIcon type="regular" iconClassName={link.icon} size="xl" className="text-white" />
                                </div>
                                <span className="flex-1 text-sm text-left">{link.label}</span>
                                <AppIcon type="regular" iconClassName="fa-chevron-right" size="sm" className="text-pale" />
                            </Link>
                        ))}
                    </div>
                </div>
            </li>
        </ul>
    )
}

function ProductDot({color}: { color: string }) {
    return <span aria-hidden="true" className={`size-2 shrink-0 rounded-full bg-${color}`} />
}

function DropDownMenu({link}: { link: NavLink }) {
    const submenuId = `submenu-${link.label.toLowerCase().replace(/\s+/g, "-")}`
    const categories = link.categories ?? []
    const themeColors = getThemeColors(link.cocoon)
    const affiliatedHref = useAffiliatedLink(false, {content: PRETTO_UTM_CONTENT.HEADER, term: PRETTO_UTM_TERM.PRET_IMMOBILIER})
    const {track} = useTracking()

    const subMenuClasses = `absolute top-full lg:top-[50px] z-10 top-[120%]
    lg:group-hover:opacity-100 opacity-0 group-hover:pointer-events-auto group-hover:h-min
    left-1/2 -translate-x-1/2`

    return (
        <ul
            data-test="header-nav-section"
            className="w-full px-2 transition-all group flex flex-1 flex-nowrap items-center rounded-lg hover:bg-pastel-neutral"
        >
            <li className="w-full">
                <Link
                    href={link.url ?? ""}
                    className={`flex items-center gap-1 xl:gap-2.5 whitespace-nowrap py-2
                        font-medium ${categories.length > 0 ? "lg:font-semibold text-sm" : "text-rm-12"}`}
                    aria-haspopup={categories.length > 0 ? "true" : undefined}
                    aria-controls={categories.length > 0 ? submenuId : undefined}
                >
                    {link.cocoon && <ProductDot color={themeColors.primary} />}
                    {link.label}
                    {categories.length > 0 && (
                        <AppIcon type="regular" size="sm" iconClassName="fa-chevron-down" className="hidden lg:block" />
                    )}
                </Link>

                {categories.length > 0 && (
                    <div
                        id={submenuId}
                        role="menu"
                        className={`rounded-[8px] bg-white lg:bg-transparent h-0
                            transition-[opacity] duration-500 ease-in-out pointer-events-none ${subMenuClasses} z-30
                            pt-[20px]
                        `}
                    >
                        <div className="bg-white rounded-3xl shadow-xl flex overflow-hidden w-[915px] h-[485px]">
                            <div className={`flex w-[144px] shrink-0 items-center justify-center bg-${themeColors.primary}`}>
                                <div
                                    className={`my-auto flex items-center justify-center h-[54px] w-[54px] rounded-full ${
                                        link.iconBgColor ? "" : `bg-${toVariant(themeColors.primary, "deep")}`
                                    }`}
                                    style={link.iconBgColor ? {backgroundColor: link.iconBgColor} : undefined}
                                >
                                    <AppIcon
                                        type="regular"
                                        iconClassName={link.icon}
                                        size="2xl"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col flex-1 overflow-hidden">
                                <div className="grid grid-cols-3 gap-x-8 p-8 flex-1 overflow-y-auto">
                                    {categories.map((category) => (
                                        <div key={category.name} className="flex flex-col items-start gap-4">
                                            <div className="text-left">
                                                <span
                                                    className={`text-xl leading-[1.55rem] title-look bg-${themeColors.secondary1} text-${themeColors.secondaryText}`}
                                                >
                                                    {category.name}
                                                </span>
                                            </div>

                                            <div className="flex flex-col gap-3 w-full">
                                                {category.links.map((catLink) => (
                                                    <Link
                                                        key={catLink.label}
                                                        href={catLink.url ?? ""}
                                                        role="menuitem"
                                                        className="flex w-full gap-4 items-center cursor-pointer
                                                            hover:bg-pastel-neutral rounded-lg px-2 py-1.5 transition-all duration-200 ease-in-out"
                                                    >
                                                        <div
                                                            className={`bg-${themeColors.secondary2} rounded-xl flex items-center justify-center shrink-0 w-[36px] h-[36px]`}
                                                        >
                                                            <AppIcon type="regular" iconClassName={catLink.icon} size="xl" />
                                                        </div>
                                                        <span className="flex-1 text-sm text-left">{catLink.label}</span>
                                                        <AppIcon type="regular" iconClassName="fa-chevron-right" size="sm" className="text-pale" />
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {link.underContent && (
                                    <div
                                        data-test="header-cta-button"
                                        className="w-full bg-neutral p-rm-25 flex justify-between items-center h-[70px]"
                                    >
                                        <div className="flex gap-rm-25 items-center">
                                            <AppIcon
                                                type="regular"
                                                iconClassName={link.underContent.icon}
                                                className="text-lg text-green-700"
                                            />

                                            <span className="w-[250px] text-left font-bold">{link.underContent.label}</span>
                                        </div>

                                        <AppButton
                                            arrowPosition="before"
                                            size="xs"
                                            href={link.underContent.url === AFFILIATED_LINK ? affiliatedHref : link.underContent.url}
                                            target={link.underContent.isExternal ? "_blank" : "_self"}
                                            onClick={() => {
                                                track({
                                                    event: "wp_advertizing_button_click",
                                                    type: "cta_header_menu",
                                                })

                                                if (link.underContent?.url === AFFILIATED_LINK) {
                                                    track({
                                                        event: "gtm_partner_link_clic",
                                                        event_source: "header_menu_cta",
                                                        partner_url: affiliatedHref,
                                                    })
                                                }
                                            }}
                                        >
                                            {link.underContent.ctaLabel}
                                        </AppButton>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </li>
        </ul>
    )
}

export default React.memo(BaseHeader)
